import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import {
  deleteRulesetDataById,
  getRulesetById,
} from '../../services/ruleset.service';
import { CONTENT_SCHEDULE_TYPE } from '../../config/const.config';
import Loading from '../table-elements/loading.component';
import useAccess from '../../hooks/access.hook';
import ConfirmPopup from '../common/confirm-popup.component';
import Iconify from '../common/iconify.component';
import AddEditScheduleRulesetRules from './add-edit-schedule-ruleset-rules.component';

const ViewScheduleRulesetRules = ({
  dataId,
  rulesetType,
  channelContentScheduleInfoId,
  showSnackbarMessage,
  handleRefreshData,
}) => {
  const { hasOperationAccess } = useAccess();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    setLoading(true);
    getRulesetById(dataId)
      .then((res) => {
        setRowData(res.data);
        setLoading(false);
      })
      .catch();
  }, [dataId]);

  const handleDeleteData = () => {
    deleteRulesetDataById(dataId)
      .then(() => {
        const message = 'Ruleset deleted successfully.';
        showSnackbarMessage(message, 'success');
        handleRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        showSnackbarMessage(message, 'error');
        setShowDeleteConfirmation(false);
      });
  };

  return (
    <>
      <Box>
        {loading && <Loading />}
        {!loading && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ backgroundColor: '#F3F6F8', mb: 1 }}
            >
              <Typography variant="h6" py={1}>
                &nbsp; {rowData.name}
              </Typography>
              <Box py={1}>
                {hasOperationAccess('rule_based_scheduling', 'manage') && (
                  <>
                    <IconButton
                      onClick={() => {
                        setShowEditDialog(true);
                      }}
                    >
                      <Iconify icon="bx:edit" />
                    </IconButton>
                    &nbsp;
                    {(rowData.isSkippedContentRule || 0) === 0 && (
                      <>
                        <IconButton
                          color="primary"
                          onClick={() => setShowDeleteConfirmation(true)}
                        >
                          <Iconify icon="material-symbols:delete-forever-outline-rounded" />
                        </IconButton>
                        &nbsp;
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
            <Grid container spacing={2} px={0.5}>
              <Grid item xs={12} sm={rulesetType === 'ASSIGNMENT' ? 6 : 4}>
                <Box fontWeight="bold">Name</Box>
                <Typography variant="body2" component="span" color="inherit">
                  {rowData.name || '-'}
                </Typography>
              </Grid>
              {(rowData.isSkippedContentRule || 0) === 0 && (
                <Grid item xs={12} sm={rulesetType === 'ASSIGNMENT' ? 6 : 4}>
                  <Box fontWeight="bold">Condition</Box>
                  <Typography variant="body2" component="span" color="inherit">
                    {rowData.rulesCondition || '-'}
                  </Typography>
                </Grid>
              )}

              {rulesetType !== 'ASSIGNMENT' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Box fontWeight="bold">
                      {rowData.type?.includes('SEASON')
                        ? 'Seasons Per Event'
                        : 'Videos Count Per Week'}
                    </Box>
                    <Typography
                      variant="body2"
                      component="span"
                      color="inherit"
                    >
                      {rowData.numberOfContentPerWeek || '0'}

                      {rowData?.dynamicContentPerWeek === 1
                        ? ' (Dynamic)'
                        : ' (Fixed)'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box fontWeight="bold">
                      {rowData.type?.includes('SEASON')
                        ? 'Time Between Season'
                        : 'Schedule X Days Advance'}
                    </Box>
                    <Typography
                      variant="body2"
                      component="span"
                      color="inherit"
                    >
                      {(rowData.type?.includes('SEASON')
                        ? rowData.numberOfDaysBetweenContent
                        : rowData.numberOfDaysInAdvance) || '0'}
                    </Typography>
                  </Grid>
                  {(rowData.isSkippedContentRule || 0) === 0 &&
                    rowData.scheduleType !== CONTENT_SCHEDULE_TYPE.HERO && (
                      <Grid item xs={12} sm={4}>
                        <Box fontWeight="bold">Content Type</Box>
                        <Typography
                          variant="body2"
                          component="span"
                          color="inherit"
                        >
                          {rowData.type.split('_')?.[0] || '-'}
                        </Typography>
                      </Grid>
                    )}
                  {rowData.type?.includes('SERIES') && (
                    <Grid item xs={12} sm={4}>
                      <Box fontWeight="bold">Series Type</Box>
                      <Typography
                        variant="body2"
                        component="span"
                        color="inherit"
                      >
                        {rowData.type.split('_')?.[1] || '-'}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {(rowData.isSkippedContentRule || 0) === 0 && (
                <Grid item xs={12} sm={12}>
                  <Typography variant="subtitle1">
                    Content Filter Logic
                  </Typography>
                  {rowData.rules?.length > 0
                    ? rowData.rules.map((item) => (
                        <Box>
                          <Typography
                            variant="body2"
                            component="span"
                            color="inherit"
                          >
                            {item.title}
                          </Typography>
                        </Box>
                      ))
                    : '-'}
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Box>

      {showEditDialog && (
        <AddEditScheduleRulesetRules
          title={`Edit Ruleset Detail - ${rowData.name}`}
          channelContentScheduleInfoId={channelContentScheduleInfoId}
          dataId={dataId}
          rulesetType={rulesetType}
          onSave={handleRefreshData}
          onCancel={() => setShowEditDialog(false)}
          showSnackbarMessage={showSnackbarMessage}
          isHeroScheduling={rowData.scheduleType === CONTENT_SCHEDULE_TYPE.HERO}
        />
      )}

      {showDeleteConfirmation && (
        <ConfirmPopup
          title="Delete Ruleset"
          message={`Do you want to delete ${
            rowData.name
          } ruleset? This will purge all the data ${
            rulesetType === 'ASSIGNMENT' ? 'assignment' : 'schedule'
          } which were using this ruleset.`}
          onCancel={() => setShowDeleteConfirmation(false)}
          onSuccess={() => handleDeleteData()}
          successButtonText="Delete"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="delete"
        />
      )}
    </>
  );
};

ViewScheduleRulesetRules.propTypes = {
  dataId: PropTypes.number.isRequired,
  rulesetType: PropTypes.string.isRequired,
  channelContentScheduleInfoId: PropTypes.number.isRequired,
  showSnackbarMessage: PropTypes.func.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
};

export default ViewScheduleRulesetRules;
